import React from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Side-by-side transcript comparison for suspected microphone cross-talk.
 *
 * Internal tool. Embeds the EXISTING ShiftTranscriptView page twice (one per
 * server) via same-origin iframes, so every feature of the real transcript page
 * — audio playback, interaction markers, verification, summaries — is preserved
 * with zero duplication. Each iframe is deep-linked with `?t=<seconds>` so it
 * auto-scrolls to and highlights the bled phrase in that shift's own timeline.
 *
 * Driven entirely by query params, so the analyze_mic_bleed.py report can emit a
 * direct `compare_url` per detected bleed event:
 *   /restaurant/transcript-compare
 *     ?shiftA=&serverA=&tA=&shiftB=&serverB=&tB=&phrase=&when=&location=
 */
const TranscriptComparisonView: React.FC = () => {
  const [params] = useSearchParams();

  const shiftA = params.get('shiftA') || '';
  const serverA = params.get('serverA') || '';
  const tA = params.get('tA') || '';
  const shiftB = params.get('shiftB') || '';
  const serverB = params.get('serverB') || '';
  const tB = params.get('tB') || '';
  const phrase = params.get('phrase') || '';
  const when = params.get('when') || '';
  const location = params.get('location') || '';

  const buildSrc = (shift: string, server: string, t: string) => {
    const base = `/restaurant/shifts/${encodeURIComponent(shift)}/transcript/${encodeURIComponent(server)}`;
    return t ? `${base}?t=${encodeURIComponent(t)}` : base;
  };

  const srcA = buildSrc(shiftA, serverA, tA);
  const srcB = buildSrc(shiftB, serverB, tB);
  const missing = !shiftA || !serverA || !shiftB || !serverB;
  const short = (id: string) => (id ? id.slice(0, 8) : '—');

  return (
    <div className="flex flex-col h-screen">
      {/* Top banner: the bleed event being examined */}
      <div className="bg-gray-900 text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-gray-400">
              Suspected mic cross-talk — shared phrase
            </div>
            {phrase && (
              <div className="text-lg font-semibold truncate">&ldquo;{phrase}&rdquo;</div>
            )}
            <div className="text-xs text-gray-400 mt-1">
              {when && <span className="mr-3">{when}</span>}
              {location && <span>location {short(location)}</span>}
            </div>
          </div>
          <div className="text-xs text-gray-300 text-right flex-shrink-0">
            <div>
              <span className="text-blue-300 font-semibold">A</span> server {short(serverA)} · shift {short(shiftA)}
              {tA && <span> · @{tA}s</span>}
            </div>
            <div>
              <span className="text-green-300 font-semibold">B</span> server {short(serverB)} · shift {short(shiftB)}
              {tB && <span> · @{tB}s</span>}
            </div>
          </div>
        </div>
      </div>

      {missing ? (
        <div className="flex-1 flex items-center justify-center text-gray-600 text-center px-6">
          Missing required params. This page needs <code className="mx-1">shiftA</code>,
          <code className="mx-1">serverA</code>, <code className="mx-1">shiftB</code>, and
          <code className="mx-1">serverB</code> (optionally <code className="mx-1">tA</code>/<code className="mx-1">tB</code>
          to jump to the phrase).
        </div>
      ) : (
        <div className="flex flex-1 min-h-0">
          <div className="flex flex-col flex-1 min-w-0 border-r border-gray-300">
            <div className="bg-blue-50 text-blue-900 text-xs font-semibold px-3 py-1 flex justify-between items-center flex-shrink-0">
              <span>Server A — {short(serverA)}</span>
              <a className="underline" href={srcA} target="_blank" rel="noreferrer">
                open in new tab ↗
              </a>
            </div>
            <iframe title="Server A transcript" src={srcA} className="flex-1 w-full border-0" />
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <div className="bg-green-50 text-green-900 text-xs font-semibold px-3 py-1 flex justify-between items-center flex-shrink-0">
              <span>Server B — {short(serverB)}</span>
              <a className="underline" href={srcB} target="_blank" rel="noreferrer">
                open in new tab ↗
              </a>
            </div>
            <iframe title="Server B transcript" src={srcB} className="flex-1 w-full border-0" />
          </div>
        </div>
      )}
    </div>
  );
};

export default TranscriptComparisonView;
